/**
 * @fileoverview Audit Log Service - เรียก API ของ /admin/audit
 *
 * ฝั่ง backend บังคับ 2 อย่างเสมอ:
 *   1. สิทธิ์ audit-logs.read
 *   2. ขอบเขตบริษัทจาก header company-uid (จัดการให้แล้วใน services/api)
 * จึงไม่ต้องส่ง company_id มาใน filter
 *
 * @module services/audit.service
 */

import { GET, GET_FILE } from './api';

const BASE_URL = 'admin/audit';

/**
 * @typedef {Object} AuditLogFilters
 * @property {number} [page]           หน้าที่ต้องการ (เริ่มที่ 1, ส่ง 0 = ไม่แบ่งหน้า)
 * @property {number} [page_size]      จำนวนต่อหน้า
 * @property {string} [data_search]    ค้นหาจากชื่อผู้ใช้ / ชื่อเอกสาร / entity
 * @property {string} [start_date]     YYYY-MM-DD
 * @property {string} [end_date]       YYYY-MM-DD (นับรวมทั้งวัน)
 * @property {string} [action]         CREATE | UPDATE | DELETE | ...
 * @property {string} [entity_type]    ชื่อ table เช่น purchase_orders
 * @property {string} [entity_id]      uid ของรายการ
 * @property {string} [actor_type]     user | external_api | ...
 * @property {string} [source_channel] admin | warehouse | external | ...
 * @property {string} [status]         success | failed
 */

/**
 * ตัดค่าว่างออกจาก query param เพื่อไม่ให้ backend เอาไปสร้างเงื่อนไขเปล่า ๆ
 * @param {Object} params
 * @returns {Object}
 */
const clean = (params = {}) => {
  const result = { ...params };
  Object.keys(result).forEach((key) => {
    if (result[key] === '' || result[key] === undefined || result[key] === null) {
      delete result[key];
    }
  });
  return result;
};

/**
 * รายการ audit log
 * @param {AuditLogFilters} filters
 */
export const getLogs = async (filters = {}) =>
  GET(BASE_URL, clean({ page: 1, page_size: 10, ...filters }));

/**
 * ประวัติทั้งหมดของเอกสาร 1 ใบ
 * @param {string} entityType ชื่อ table เช่น purchase_orders
 * @param {string} entityId   uid ของรายการ
 * @param {{page?: number, page_size?: number}} [options]
 */
export const getByEntity = async (entityType, entityId, options = {}) =>
  GET(`${BASE_URL}/entity/${entityType}/${entityId}`, clean({ page: 1, page_size: 20, ...options }));

/**
 * ประวัติของผู้ใช้รายคน
 * @param {string} userUid uid ของผู้ใช้ (ไม่ใช่ id)
 * @param {Object} [options]
 */
export const getByUser = async (userUid, options = {}) =>
  GET(`${BASE_URL}/user/${userUid}`, clean({ page: 1, page_size: 20, ...options }));

/**
 * สถิติสรุป (ยอดรวม / แยกตาม action / ช่องทาง / entity / ผู้ใช้ / รายวัน)
 * @param {{start_date?: string, end_date?: string}} [options]
 */
export const getStats = async (options = {}) => GET(`${BASE_URL}/stats`, clean(options));

/**
 * ค่าที่ใช้เติม dropdown ของ filter
 */
export const getFilterOptions = async () => GET(`${BASE_URL}/filters`, {});

/**
 * audit log รายตัว (มี old_values / new_values ครบ)
 * @param {string} uid
 */
export const getByUid = async (uid) => GET(`${BASE_URL}/${uid}`, {});

/**
 * รายการ API request log (log เชิงเทคนิค คนละตารางกับ audit log)
 * @param {Object} [filters]
 * @param {"true"} [filters.include_unscoped] รวม call ที่ยืนยันตัวตนไม่ผ่าน (401)
 *   ซึ่งยังไม่รู้ว่าเป็น token ของบริษัทไหน จึงไม่ติดมากับ company scope ปกติ
 */
export const getRequestLogs = async (filters = {}) =>
  GET(`${BASE_URL}/requests`, clean({ page: 1, page_size: 10, ...filters }));

/**
 * API request log รายตัว (มี req/res body ครบ)
 * @param {string} uid
 */
export const getRequestLogByUid = async (uid) => GET(`${BASE_URL}/requests/${uid}`, {});

/**
 * HTTP call ทั้งหมดที่เกิดใน request เดียวกัน - ใช้กดจาก audit log ไปดู raw request
 * @param {string} requestId
 */
export const getByRequestId = async (requestId) => GET(`${BASE_URL}/correlation/${requestId}`, {});

/**
 * ดาวน์โหลดไฟล์ export
 * backend ตอบเป็นไฟล์ตรง ๆ จึงต้องใช้ GET_FILE (GET ปกติจะ parse เป็น JSON แล้วพัง)
 * ส่ง page: 0 เพื่อไม่แบ่งหน้า - backend cap ไว้ที่ 10,000 แถวและแจ้งผ่าน header X-Export-Truncated
 * @param {AuditLogFilters} filters
 * @param {"csv"|"xlsx"} [format]
 * @returns {Promise<{status: boolean, blob?: Blob, message?: string}>}
 */
export const exportLogs = async (filters = {}, format = 'csv') =>
  GET_FILE(`${BASE_URL}/export`, clean({ ...filters, page: 0, export_format: format }));

/**
 * ชื่อเดิมของ getByUid - คงไว้ให้โค้ดเก่าที่ยังอ้างอยู่เรียกได้
 * @param {string} uid
 */
export const getById = async (uid) => getByUid(uid);

/**
 * ประเภทการกระทำ - ต้องตรงกับ config/audit.constant.js ฝั่ง backend
 */
export const ACTION_TYPES = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  RESTORE: 'RESTORE',
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
  CANCEL: 'CANCEL',
  IMPORT: 'IMPORT',
  EXPORT: 'EXPORT',
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  LOGIN_FAILED: 'LOGIN_FAILED',
  PERMISSION_CHANGE: 'PERMISSION_CHANGE',
  SEND_EMAIL: 'SEND_EMAIL',
};

/**
 * สีของ badge ต่อ action (ใช้ class ของ bootstrap ตาม pattern หน้าอื่นในโปรเจกต์)
 * @param {string} action
 * @returns {{label: string, className: string}}
 */
export const getActionConfig = (action) => {
  const configs = {
    CREATE: { label: 'CREATE', className: 'text-success bg-success-light' },
    UPDATE: { label: 'UPDATE', className: 'text-warning bg-warning-light' },
    DELETE: { label: 'DELETE', className: 'text-danger bg-danger-light' },
    RESTORE: { label: 'RESTORE', className: 'text-info bg-info-light' },
    APPROVE: { label: 'APPROVE', className: 'text-success bg-success-light' },
    REJECT: { label: 'REJECT', className: 'text-danger bg-danger-light' },
    CANCEL: { label: 'CANCEL', className: 'text-secondary bg-light' },
    IMPORT: { label: 'IMPORT', className: 'text-primary bg-primary-light' },
    EXPORT: { label: 'EXPORT', className: 'text-primary bg-primary-light' },
    LOGIN: { label: 'LOGIN', className: 'text-primary bg-primary-light' },
    LOGOUT: { label: 'LOGOUT', className: 'text-secondary bg-light' },
    LOGIN_FAILED: { label: 'LOGIN FAILED', className: 'text-danger bg-danger-light' },
    PERMISSION_CHANGE: { label: 'PERMISSION', className: 'text-warning bg-warning-light' },
    SEND_EMAIL: { label: 'SEND EMAIL', className: 'text-info bg-info-light' },
  };

  return configs[action] || { label: action || '-', className: 'text-secondary bg-light' };
};

const auditService = {
  getLogs,
  getByEntity,
  getByUser,
  getStats,
  getFilterOptions,
  getByUid,
  getById,
  exportLogs,
  getRequestLogs,
  getRequestLogByUid,
  getByRequestId,
  ACTION_TYPES,
  getActionConfig,
};

export default auditService;
